import PRICE_TYPES from './priceTypes'
import getSelectedBetsForMultiples from './getSelectedBetsForMultiples'
import { isEmptyStake } from './calculateStake'

// Functions that map Redux state objects to API V2 request objects
// Note that API V2 uses the JSON API specification

const EVENS = 'Evs'

const getFractions = (bet) => {
    if (bet.fractionalPrice === EVENS) {
        return [1, 1];
    }

    if (bet.fractionalPrice === PRICE_TYPES.SP) {
        return [null, null];
    }

    return bet.fractionalPrice.split('/').map(num => parseInt(num, 10));
}

const getPriceDetails = (bet) => {
    if (bet.priceType === PRICE_TYPES.SP) {
        return {
            isStartingPrice: true,
            priceNumerator: null,
            priceDenominator: null,
        }
    }

    const [numerator, denominator] = getFractions(bet)
    return {
        isStartingPrice: false,
        priceNumerator: numerator,
        priceDenominator: denominator,
    }
}

const transformSingles = stakedSingles => stakedSingles.map((single) => {
    const betPriceDetails = getPriceDetails(single)
    const useBog = (single.priceType === PRICE_TYPES.SP) ?
            false :
            single.isBog === 'Y'

    return {
        id: single.associatedBet.optionId,
        type: 'bets',
        attributes: {
            betType: 'single',
            stakePerLine: parseFloat(single.stake),
            isEachWay: single.isEachWay,
            betSelections: [
                {
                    marketId: single.meetingId,
                    eventId: single.eventId,
                    selectionId: single.outcomersId,
                    ...betPriceDetails,
                    useBestOddsGuaranteed: useBog,
                },
            ],
        },
    }
})

const transformMultiples = (stakedMultiples, selectedBets) => stakedMultiples.map((multiple) => {
    const betSelections = getSelectedBetsForMultiples(multiple.betSelections, selectedBets)
    const multipleSelections = betSelections.map((bet) => {
        const betPriceDetails = getPriceDetails(bet)
        const useBog = (bet.priceType === PRICE_TYPES.SP) ?
                false :
                bet.isBog === 'Y'

        return {
            marketId: bet.meetingId,
            eventId: bet.eventId,
            selectionId: bet.outcomersId,
            ...betPriceDetails,
            useBestOddsGuaranteed: useBog,
        }
    });

    return {
        id: multiple.optionId,
        type: 'bets',
        attributes: {
            betType: multiple.betType,
            stakePerLine: parseFloat(multiple.stakeValue),
            isEachWay: multiple.isEachWay,
            betSelections: multipleSelections,
        },
    }
})

export const createPlaceBetRequest = (selectedBets, stakedMultiples, selectedFreeBetId) => {
    const betInfo = {
        data: [],
    }

    // Only attempt to place singles that have a proper stake
    const stakedSingles = selectedBets.filter(
        bet => !isEmptyStake(bet.stake),
    )

    // Single bets...
    betInfo.data = betInfo.data.concat(
        transformSingles(stakedSingles),
    )

    // Multiples...
    betInfo.data = betInfo.data.concat(
        transformMultiples(stakedMultiples, selectedBets),
    )

    // Free bets (do not send if none applied)
    if (selectedFreeBetId) {
        betInfo.meta = {
            freeBetId: selectedFreeBetId,
        }
    }

    return betInfo
}

/**
 * Generates an API V2 friendly object for POSTing to the build bets
 * endpoint.
 *
 * @param  {Array.<Object>} selectedBets - from bookmakersInfo
 *
 * This request should contain data about the selected bets in the following
 * format:
 *
 *   {
 *      "data": [
 *           {
 *                "id": "foo", // outcomersId
 *                "type": "bet-selections",
 *                "attributes": {
 *                    "marketId": "foo" // eventId
 *                    "priceNumerator": 51,
 *                    "priceDenominator": 51
 *                }
 *            }
 *        ]
 *    }
 *
 */
export const createBuildBetRequest = selectedBets => ({
    data: selectedBets.map((bet) => {
        const [numerator, denominator] = getFractions(bet)

        return {
            id: bet.outcomersId.toString(),
            type: 'bet-selections',
            attributes: {
                marketId: bet.meetingId,
                eventId: bet.eventId,
                priceNumerator: numerator,
                priceDenominator: denominator,
            },
        }
    }),
})



// WEBPACK FOOTER //
// /src/js/common/utils/apiTransformers.js